<script setup>
import {ref, reactive, onMounted, onBeforeUnmount} from 'vue'

const props = defineProps({
    // The currently applied search parameters. Only orderBy and perPage are edited here.
    searchParameters: {
        type: Object,
        required: true,
    },
})

const emit = defineEmits(['apply'])

const defaultSorting = () => ({
    orderBy: 'updated-lastest',
    perPage: '25',
})

const pickSorting = (source) => ({
    orderBy: source.orderBy,
    perPage: String(source.perPage),
})

const root = ref(null)
const open = ref(false)
const draft = reactive(defaultSorting())

const orderByOptions = [
    { label: _mft('crm:filters.orderBy.updatedDesc'), value: 'updated-lastest' },
    { label: _mft('crm:filters.orderBy.updatedAsc'), value: 'updated-oldest' },
    { label: _mft('crm:filters.orderBy.createdDesc'), value: 'created-newest' },
    { label: _mft('crm:filters.orderBy.createdAsc'), value: 'created-oldest' },
    { label: _mft('crm:filters.orderBy.nameAsc'), value: 'name-a' },
    { label: _mft('crm:filters.orderBy.nameDesc'), value: 'name-z' },
]

const perPageOptions = [
    { label: '25', name: '25' },
    { label: '100', name: '100' },
    { label: '250', name: '250' },
]

const openPanel = () => {
    Object.assign(draft, pickSorting(props.searchParameters))
    open.value = true
}

const closePanel = () => {
    open.value = false
}

const togglePanel = () => {
    open.value ? closePanel() : openPanel()
}

const reset = () => {
    Object.assign(draft, defaultSorting())
}

const apply = () => {
    emit('apply', pickSorting(draft))
    closePanel()
}

const onKeydown = (e) => {
    if (e.key === 'Escape' && open.value) {
        closePanel()
    }
}

// pointerdown rather than click: a click can detach its target (e.g. removing a
// tag chip) before this runs, which would make it look like an outside click
const onPointerdown = (e) => {
    if (open.value && root.value && ! root.value.contains(e.target)) {
        closePanel()
    }
}

onMounted(() => {
    window.addEventListener('keydown', onKeydown)
    document.addEventListener('pointerdown', onPointerdown)
})

onBeforeUnmount(() => {
    window.removeEventListener('keydown', onKeydown)
    document.removeEventListener('pointerdown', onPointerdown)
})
</script>

<template>
    <div ref="root">
        <button-base
            size="lg"
            :style-type="open ? 'tinted' : 'secondary'"
            :aria-expanded="open"
            @click="togglePanel"
        >
            <template #icon>
                <i class="fa-light fa-arrow-up-arrow-down"></i>
            </template>
            <span>{{ _mft('crm:filters.sort.button') }}</span>
        </button-base>

        <!-- Positioned against the nearest `relative` ancestor (the search row),
             so the panel lines up with the row's right edge on every screen size -->
        <div
            v-show="open"
            class="absolute right-0 z-50 w-full p-6 mt-2 bg-white border border-gray-200 shadow-xl top-full sm:w-[36rem] rounded-2xl"
        >
            <div class="grid items-center grid-cols-1 sm:grid-cols-[10rem_1fr] gap-x-4 gap-y-4">
                <label class="font-medium text-gray-700">
                    {{ _mft('crm:filters.orderBy.title') }}
                </label>
                <input-select
                    :content="draft.orderBy"
                    :options="orderByOptions"
                    @update:content="draft.orderBy = $event"
                    class="!mb-0"
                />

                <label class="font-medium text-gray-700 first-letter:uppercase">
                    {{ _mft('crm:perPage') }}
                </label>
                <button-bar
                    :options="perPageOptions"
                    :selected-option-name="draft.perPage"
                    :responsive="false"
                    size="md"
                    class="!mb-0"
                    @select="draft.perPage = $event.name"
                />
            </div>

            <hr class="my-5 border-gray-100">

            <div class="flex items-center justify-between gap-2">
                <button
                    type="button"
                    class="text-sm text-gray-700 mf-link"
                    @click="reset"
                >
                    {{ _mft('crm:filters.resetToDefault') }}
                </button>

                <div class="flex items-center gap-2">
                    <button-base
                        style-type="secondary"
                        @click="closePanel"
                    >
                        {{ _mft('shared:action.cancel') }}
                    </button-base>
                    <button-base
                        style-type="primary"
                        @click="apply"
                    >
                        {{ _mft('crm:filters.apply') }}
                    </button-base>
                </div>
            </div>
        </div>
    </div>
</template>

<style>

</style>
